import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import { LuBriefcaseBusiness, LuClock3, LuLoaderCircle, LuPencil, LuPercent, LuSave } from 'react-icons/lu';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import { Field, SelectInput, TextArea, TextInput } from '../../components/form/FormControls';
import useTariffText from './tariffText';
import FteValuesEditor from './parts/FteValuesEditor';
import ExistingTariffs from './parts/ExistingTariffs';

const truncateText = (text, maxLength = 40) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * "Yeni Tarifeler Ekle" / "Tarife Düzenle". A new tariff can cover several
 * projects of a customer at once (projects that already have tariffs are
 * skipped); FTE tariffs get one record per selected value, hourly ones one per
 * value with the same rate. The data logic is unchanged.
 */
const AddTariffsModal = ({ show, handleClose, selectedTariff, preselectedCustomer, preselectedProject }) => {
  const { t, language, label, typeLabel } = useTariffText();
  // Toasts read the current language through a ref
  const labelRef = useRef(label);
  labelRef.current = label;
  const msg = (key, vars) => labelRef.current(`toast.${key}`, vars);
  const [formData, setFormData] = useState({
    customer: '',
    projects: [], // Changed from 'project' to 'projects' array
    type: '',
    currency: '',
    date: '',
    info: '',
    isSubcontractor: '',
    hourlyRate: '',
    percentageIncrease: 0,
  });
  const [tariffValues, setTariffValues] = useState([
    { value: '0.0', price: '', selected: false, existingId: null },
    { value: '0.1', price: '', selected: false, existingId: null },
    { value: '0.2', price: '', selected: false, existingId: null },
    { value: '0.3', price: '', selected: false, existingId: null },
    { value: '0.4', price: '', selected: false, existingId: null },
    { value: '0.5', price: '', selected: false, existingId: null },
    { value: '0.6', price: '', selected: false, existingId: null },
    { value: '0.7', price: '', selected: false, existingId: null },
    { value: '0.8', price: '', selected: false, existingId: null },
    { value: '0.9', price: '', selected: false, existingId: null },
    { value: '1.0', price: '', selected: false, existingId: null },
  ]);
  const [defaultPrice, setDefaultPrice] = useState('');
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingTariffs, setExistingTariffs] = useState([]);
  const [isLoadingExistingTariffs, setIsLoadingExistingTariffs] =
    useState(false);
  const [existingTariffRecords, setExistingTariffRecords] = useState([]);
  const [projectsWithTariffs, setProjectsWithTariffs] = useState({}); // Track which projects have existing tariffs

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setIsLoadingCustomers(true);
        const customersSnapshot = await getDocs(collection(db, 'Customers'));
        const customersList = customersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setCustomers(customersList);
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast.error(msg('customersError'));
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    if (show) {
      fetchCustomers();
    }
  }, [show]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!formData.customer) {
        setProjects([]);
        return;
      }

      try {
        setIsLoadingProjects(true);
        const projectsSnapshot = await getDocs(collection(db, 'Projects'));
        const selectedCustomer = customers.find(
          (c) => c.id === formData.customer
        );

        if (!selectedCustomer) {
          setProjects([]);
          return;
        }

        const projectsList = projectsSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((project) => project.customer === selectedCustomer.Başlık);

        setProjects(projectsList);
      } catch (error) {
        console.error('Error fetching projects:', error);
        toast.error(msg('projectsError'));
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [formData.customer, customers]);

  useEffect(() => {
    if (selectedTariff) {
      setFormData({
        customer: selectedTariff.customer || '',
        projects: selectedTariff.project ? [selectedTariff.project] : [], // Convert single project to array
        type: selectedTariff.type || '',
        currency: selectedTariff.currency || '',
        date: selectedTariff.date || '',
        info: selectedTariff.info || '',
        isSubcontractor: selectedTariff.isSubcontractor || '',
        hourlyRate: selectedTariff.price || '',
        percentageIncrease: selectedTariff.percentageIncrease || 0,
      });

      // If we're in edit mode and have a selected tariff with FTE value
      if (selectedTariff.type === 'FTE' && selectedTariff.value) {
        setTariffValues((prev) =>
          prev.map((tv) =>
            tv.value === selectedTariff.value ? { ...tv, selected: true } : tv
          )
        );
      } else {
        setTariffValues([
          { value: '0.0', price: '', selected: false, existingId: null },
          { value: '0.1', price: '', selected: false, existingId: null },
          { value: '0.2', price: '', selected: false, existingId: null },
          { value: '0.3', price: '', selected: false, existingId: null },
          { value: '0.4', price: '', selected: false, existingId: null },
          { value: '0.5', price: '', selected: false, existingId: null },
          { value: '0.6', price: '', selected: false, existingId: null },
          { value: '0.7', price: '', selected: false, existingId: null },
          { value: '0.8', price: '', selected: false, existingId: null },
          { value: '0.9', price: '', selected: false, existingId: null },
          { value: '1.0', price: '', selected: false, existingId: null },
        ]);
      }
    } else {
      setFormData({
        customer: preselectedCustomer || '',
        projects: preselectedProject ? [preselectedProject] : [], // Convert to array
        type: '',
        currency: '',
        date: '',
        info: '',
        isSubcontractor: '',
        hourlyRate: '',
        percentageIncrease: 0,
      });
      // Pre-select all FTE values when adding a new tariff
      setTariffValues([
        { value: '0.0', price: '', selected: true, existingId: null },
        { value: '0.1', price: '', selected: true, existingId: null },
        { value: '0.2', price: '', selected: true, existingId: null },
        { value: '0.3', price: '', selected: true, existingId: null },
        { value: '0.4', price: '', selected: true, existingId: null },
        { value: '0.5', price: '', selected: true, existingId: null },
        { value: '0.6', price: '', selected: true, existingId: null },
        { value: '0.7', price: '', selected: true, existingId: null },
        { value: '0.8', price: '', selected: true, existingId: null },
        { value: '0.9', price: '', selected: true, existingId: null },
        { value: '1.0', price: '', selected: true, existingId: null },
      ]);
    }
  }, [selectedTariff, show, preselectedCustomer, preselectedProject]);

  useEffect(() => {
    const fetchExistingTariffs = async () => {
      if (!formData.customer || formData.projects.length === 0) {
        setExistingTariffs([]);
        setExistingTariffRecords([]);
        setProjectsWithTariffs({});
        return;
      }

      setIsLoadingExistingTariffs(true);
      try {
        // Check each selected project for existing tariffs
        const projectTariffMap = {};

        for (const projectId of formData.projects) {
          const tariffsQuery = query(
            collection(db, 'Tariffs'),
            where('customer', '==', formData.customer),
            where('project', '==', projectId)
          );
          const tariffsSnapshot = await getDocs(tariffsQuery);
          const tariffsList = tariffsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          // Store tariffs for this project
          projectTariffMap[projectId] = tariffsList;
        }

        setProjectsWithTariffs(projectTariffMap);

        // Flatten all tariffs for display purposes
        const allTariffs = Object.values(projectTariffMap).flat();
        setExistingTariffRecords(allTariffs);

        // Update tariffValues to mark existing values as selected (only for single project edit mode)
        if (formData.type === 'FTE' && formData.projects.length === 1) {
          const tariffsList = projectTariffMap[formData.projects[0]] || [];
          const existingFTEValues = tariffsList
            .filter((t) => t.type === 'FTE' && t.active !== false)
            .map((t) => t.value);

          setTariffValues((prev) =>
            prev.map((tv) => {
              const existingTariff = tariffsList.find((t) => t.value === tv.value);
              return {
                ...tv,
                selected: existingFTEValues.includes(tv.value) || tv.selected,
                existingId: existingTariff?.id || null,
                // Mevcut tarifelerin fiyat bilgisini yükle, ama sadece existingId varsa
                price: existingTariff?.id ? (existingTariff.price || '') : tv.price,
              };
            })
          );
        }

        const existingValues = allTariffs.map((t) => t.value);
        existingValues.sort((a, b) => parseFloat(a) - parseFloat(b));
        setExistingTariffs(existingValues);
      } catch (error) {
        console.error('Error fetching existing tariffs:', error);
        toast.error(msg('existingError'));
        setExistingTariffs([]);
        setExistingTariffRecords([]);
        setProjectsWithTariffs({});
      } finally {
        setIsLoadingExistingTariffs(false);
      }
    };

    if (show) {
      fetchExistingTariffs();
    }
  }, [show, formData.customer, formData.projects, formData.type]);

  const handleCustomerChange = (e) => {
    const newCustomer = e.target.value;
    setFormData({
      ...formData,
      customer: newCustomer,
      projects: [], // Reset projects array when customer changes
    });
  };

  const handleProjectToggle = (projectId) => {
    setFormData((prev) => {
      const isSelected = prev.projects.includes(projectId);
      return {
        ...prev,
        projects: isSelected
          ? prev.projects.filter((id) => id !== projectId)
          : [...prev.projects, projectId],
      };
    });
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({
      ...formData,
      type: newType,
    });

    // Reset the tariff values when type changes
    if (newType === 'FTE') {
      // For new tariffs, pre-select all FTE values
      // For editing existing tariffs, keep the current behavior
      setTariffValues((prevValues) =>
        prevValues.map((v) => ({ 
          ...v, 
          price: '', 
          selected: selectedTariff ? false : true // Pre-select all for new tariffs
        }))
      );
    }
  };

  const handleTariffToggle = (index) => {
    if (formData.type !== 'FTE') return;

    const updatedTariffValues = [...tariffValues];
    const wasSelected = updatedTariffValues[index].selected;
    updatedTariffValues[index].selected = !updatedTariffValues[index].selected;
    
    // Eğer FTE değeri seçiliyorsa ve varsayılan fiyat varsa, o fiyatı uygula
    // Hem mevcut tarifelere hem de yeni seçilenlere uygula
    if (!wasSelected && updatedTariffValues[index].selected && defaultPrice) {
      updatedTariffValues[index].price = defaultPrice;
    }
    
    setTariffValues(updatedTariffValues);
  };

  const handleDefaultPriceChange = (e) => {
    const price = e.target.value;
    setDefaultPrice(price);
    
    // Varsayılan fiyat değişikliği sadece yeni seçimlere uygulanır
    // Ama mevcut seçili olanların fiyat alanlarında da gösterilir (görsel olarak)
    if (price) {
      const updatedTariffValues = tariffValues.map(tariff => ({
        ...tariff,
        // Seçili olan ve fiyat alanı boş, 0 veya mevcut varsayılan fiyatla aynı olan değerlere uygula
        price: tariff.selected && (!tariff.price || tariff.price === '0' || tariff.price === 0 || tariff.price === defaultPrice) ? price : tariff.price
      }));
      setTariffValues(updatedTariffValues);
    }
  };

  const handleIndividualPriceChange = (index, price) => {
    const updatedTariffValues = [...tariffValues];
    updatedTariffValues[index].price = price;
    setTariffValues(updatedTariffValues);
  };

  const resetModalState = () => {
    setFormData({
      customer: '',
      projects: [], // Reset to empty array
      type: '',
      currency: '',
      date: '',
      info: '',
      isSubcontractor: '',
      hourlyRate: '',
      percentageIncrease: 0,
    });
    // Pre-select all FTE values for new tariffs
    setTariffValues([
      { value: '0.0', price: '', selected: true, existingId: null },
      { value: '0.1', price: '', selected: true, existingId: null },
      { value: '0.2', price: '', selected: true, existingId: null },
      { value: '0.3', price: '', selected: true, existingId: null },
      { value: '0.4', price: '', selected: true, existingId: null },
      { value: '0.5', price: '', selected: true, existingId: null },
      { value: '0.6', price: '', selected: true, existingId: null },
      { value: '0.7', price: '', selected: true, existingId: null },
      { value: '0.8', price: '', selected: true, existingId: null },
      { value: '0.9', price: '', selected: true, existingId: null },
      { value: '1.0', price: '', selected: true, existingId: null },
    ]);
    setDefaultPrice('');
    setProjectsWithTariffs({}); // Reset projects with tariffs tracking
  };

  const handleModalClose = () => {
    resetModalState();
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (
        !formData.customer ||
        formData.projects.length === 0 ||
        !formData.type ||
        !formData.currency ||
        !formData.date ||
        !formData.isSubcontractor
      ) {
        toast.error(msg('required'));
        return;
      }

      if (formData.type === 'Saat' && !formData.hourlyRate) {
        toast.error(msg('hourlyRequired'));
        return;
      }

      if (formData.type === 'FTE') {
        const selectedFTEValues = tariffValues.filter((tv) => tv.selected);
        if (selectedFTEValues.length === 0 && !selectedTariff) {
          // For new tariffs, this check is not needed since all values are pre-selected
          // For editing existing tariffs, we still want this validation
          toast.error(msg('fteRequired'));
          return;
        }
      }

      setIsSaving(true);

      // If editing an existing tariff
      if (selectedTariff) {
        if (formData.type === 'Saat') {
          // Update hourly rate tariff
          await updateDoc(doc(db, 'Tariffs', selectedTariff.id), {
            ...formData,
            price: Number(formData.hourlyRate),
          });
          toast.success(msg('updated'));
        } else {
          // For FTE type, handle all selected/unselected values
          let updateCount = 0;
          let createCount = 0;
          let deactivateCount = 0;

          // Process all tariff values
          for (const tariff of tariffValues) {
            if (tariff.selected) {
              if (tariff.existingId) {
                // Update existing tariff
                await updateDoc(doc(db, 'Tariffs', tariff.existingId), {
                  ...formData,
                  value: tariff.value,
                  price: Number(tariff.price) || 0,
                  active: true,
                });
                updateCount++;
              } else {
                // Create new tariff
                await addDoc(collection(db, 'Tariffs'), {
                  ...formData,
                  value: tariff.value,
                  price: Number(tariff.price) || 0,
                  active: true,
                });
                createCount++;
              }
            } else if (tariff.existingId) {
              // Deactivate unselected existing tariff
              await updateDoc(doc(db, 'Tariffs', tariff.existingId), {
                ...formData,
                value: tariff.value,
                price: 0,
                active: false,
              });
              deactivateCount++;
            }
          }

          // Show summary of changes
          if (updateCount > 0) {
            toast.success(msg('updatedCount', { count: updateCount }));
          }
          if (createCount > 0) {
            toast.success(msg('createdCount', { count: createCount }));
          }
          if (deactivateCount > 0) {
            toast.info(msg('deactivatedCount', { count: deactivateCount }));
          }
        }
      } else {
        // Creating new tariffs for multiple projects
        let totalCreatedCount = 0;
        let skippedProjects = [];
        const projectsToProcess = formData.projects;

        for (const projectId of projectsToProcess) {
          // Check if this project has existing tariffs
          const projectTariffs = projectsWithTariffs[projectId] || [];
          const hasExistingTariffs = projectTariffs.length > 0;

          if (hasExistingTariffs) {
            // Skip this project and add to warning list
            const project = projects.find((p) => p.id === projectId);
            skippedProjects.push(project?.titleShort || projectId);
            continue;
          }

          // Create tariffs for this project
          if (formData.type === 'Saat') {
            // For "Saat" type, create an entry for each tariff value with the hourly rate
            for (const tariff of tariffValues) {
              const tariffData = {
                ...formData,
                project: projectId, // Use single project ID for each record
                value: tariff.value,
                price: Number(formData.hourlyRate),
                active: true,
              };
              // Remove the projects array from tariffData
              delete tariffData.projects;
              await addDoc(collection(db, 'Tariffs'), tariffData);
              totalCreatedCount++;
            }
          } else {
            // For "FTE" type, only create entries for selected values
            const selectedFTEValues = tariffValues.filter((tv) => tv.selected);
            for (const tariff of selectedFTEValues) {
              const tariffData = {
                ...formData,
                project: projectId, // Use single project ID for each record
                value: tariff.value,
                price: Number(tariff.price) || 0,
                active: true,
              };
              // Remove the projects array from tariffData
              delete tariffData.projects;
              await addDoc(collection(db, 'Tariffs'), tariffData);
              totalCreatedCount++;
            }
          }
        }

        // Show results
        if (totalCreatedCount > 0) {
          toast.success(msg('createdCount', { count: totalCreatedCount }));
        }

        if (skippedProjects.length > 0) {
          toast.warning(
            msg('skipped', { projects: skippedProjects.join(', ') }),
            { autoClose: 5000 }
          );
        }

        if (totalCreatedCount === 0 && skippedProjects.length === 0) {
          toast.info(msg('nothingToAdd'));
        }
      }

      resetModalState();
      handleClose();
    } catch (error) {
      console.error('Error adding/updating tariffs:', error);
      toast.error(msg('saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  // FTE: seçili ve fiyatı girili değerlere yüzde zam (eski "Uygula" düğmesinin kodu)
  const applyFtePercentage = () => {
    const percentageInput = document.getElementById('ftePercentageInput');
    const percentage = parseFloat(percentageInput.value) || 0;

    if (percentage === 0) {
      toast.info(msg('percentRequired'));
      return;
    }

    const selectedTariffs = tariffValues.filter(t => t.selected && t.price);
    if (selectedTariffs.length === 0) {
      toast.info(msg('noPricedSelection'));
      return;
    }

    const updatedTariffValues = tariffValues.map(tariff => {
      if (tariff.selected && tariff.price) {
        const currentPrice = parseFloat(tariff.price) || 0;
        const newPrice = currentPrice + (currentPrice * percentage) / 100;
        return { ...tariff, price: newPrice.toFixed(2) };
      }
      return tariff;
    });

    setTariffValues(updatedTariffValues);
    toast.success(msg('percentApplied', { percent: percentage }));
  };

  // Saat: saatlik ücrete yüzde zam (eski "Uygula" düğmesinin kodu)
  const applyHourlyPercentage = () => {
    const percentage = formData.percentageIncrease || 0;
    if (percentage <= 0) {
      toast.info(msg('positivePercent'));
      return;
    }

    const currentRate =
      parseFloat(formData.hourlyRate) || 0;
    if (currentRate <= 0) {
      toast.info(msg('hourlyFirst'));
      return;
    }

    // Calculate the new rate with the percentage increase
    const newRate =
      currentRate + (currentRate * percentage) / 100;
    setFormData((prev) => ({
      ...prev,
      hourlyRate: newRate.toFixed(2),
    }));

    toast.success(msg('hourlyRaised', { percent: percentage }));
  };

  const isEdit = Boolean(selectedTariff);
  const PANEL = 'tw-space-y-4 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/60 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.02]';

  return (
    <Modal
      isOpen={show}
      onClose={handleModalClose}
      size="xl"
      title={isEdit ? label('modal.titleEdit') : label('modal.titleAdd')}
      subtitle={isEdit ? `${typeLabel(selectedTariff.type)} ${selectedTariff.value || ''} · ${selectedTariff.projectName || ''}` : label('modal.subtitleAdd')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient={isEdit ? 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' : 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30'} size="xs">
          {isEdit ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuBriefcaseBusiness aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={handleModalClose}>{t('common.close')}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isSaving ? label('modal.saving') : isEdit ? label('modal.update') : label('modal.save')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} className="tw-space-y-6">
        <div className="tw-grid tw-gap-6 lg:tw-grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          {/* Sol: müşteri, projeler ve tarife bilgileri */}
          <div className="tw-space-y-4">
            <Field label={label('modal.customer')} htmlFor="tariffCustomer" required hint={isLoadingCustomers ? label('modal.customersLoading') : undefined}>
              <SelectInput
                id="tariffCustomer"
                value={formData.customer}
                onChange={handleCustomerChange}
                required
                disabled={isLoadingCustomers}
                placeholder={label('modal.choose')}
                options={customers.map((customer) => ({ value: customer.id, label: truncateText(customer.Başlık) }))}
              />
            </Field>

            <Field label={label('modal.projects')} required hint={formData.projects.length > 0 ? label('modal.projectsSelected', { count: formData.projects.length }) : undefined}>
              {isLoadingProjects && <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('modal.projectsLoading')}</p>}
              {!formData.customer ? (
                <p className="tw-rounded-xl tw-bg-slate-500/[0.06] tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">{label('modal.chooseCustomerFirst')}</p>
              ) : projects.length === 0 ? (
                <p className="tw-rounded-xl tw-bg-slate-500/[0.06] tw-px-3 tw-py-2.5 tw-text-sm tw-text-slate-500 dark:tw-bg-white/[0.04] dark:tw-text-slate-400">{label('modal.noProjects')}</p>
              ) : (
                <ul className="tw-max-h-52 tw-space-y-1 tw-overflow-y-auto tw-rounded-2xl tw-border tw-border-slate-200/80 tw-p-1.5 glass-scrollbar dark:tw-border-white/10">
                  {projects.map((project) => {
                    const isSelected = formData.projects.includes(project.id);
                    const hasTariffs = projectsWithTariffs[project.id]?.length > 0;
                    return (
                      <li key={project.id} className="tw-relative">
                        <label className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-2.5 tw-py-2 tw-text-sm tw-transition-colors ${hasTariffs ? 'tw-bg-rose-500/[0.07]' : isSelected ? 'tw-bg-teal-500/[0.07]' : 'hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.04]'}`}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleProjectToggle(project.id)}
                            className="tw-h-4 tw-w-4 tw-shrink-0 tw-cursor-pointer tw-accent-teal-600"
                          />
                          <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{project.titleShort}</span>
                          {hasTariffs && <span className="tw-shrink-0 tw-text-[11px] tw-font-bold tw-text-rose-600 dark:tw-text-rose-300">{label('modal.hasTariffs')}</span>}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Field>

            {/* Seçili projelerin kur sabitlemesi */}
            {formData.projects.length > 0 && (
              <div className="tw-space-y-1.5 tw-rounded-2xl tw-bg-sky-500/[0.06] tw-p-3">
                <p className="tw-text-xs tw-font-semibold tw-text-sky-900 dark:tw-text-sky-100">{label('modal.currencyInfo')}</p>
                {formData.projects.map((projectId) => {
                  const project = projects.find((p) => p.id === projectId);
                  if (!project) return null;
                  const isFixed = !!project.isCurrencyFixed;
                  const rate = project.fixedCurrencyRate;
                  return (
                    <p key={projectId} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
                      <span className="tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{project.titleShort}</span>
                      <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-font-semibold ${isFixed ? 'tw-bg-amber-500/10 tw-text-amber-700 dark:tw-text-amber-300' : 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300'}`}>
                        {isFixed ? label('modal.currencyFixed', { rate }) : label('modal.currencyNotFixed')}
                      </span>
                    </p>
                  );
                })}
              </div>
            )}

            <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
              <Field label={label('modal.type')} htmlFor="tariffType" required>
                <SelectInput
                  id="tariffType"
                  value={formData.type}
                  onChange={handleTypeChange}
                  required
                  placeholder={label('modal.choose')}
                  options={[{ value: 'FTE', label: 'FTE' }, { value: 'Saat', label: label('types.hour') }]}
                />
              </Field>
              <Field label={label('modal.currency')} htmlFor="tariffCurrency" required>
                <SelectInput
                  id="tariffCurrency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  required
                  placeholder={label('modal.choose')}
                  options={['TRY', 'USD', 'EUR', 'GBP'].map((code) => ({ value: code, label: code }))}
                />
              </Field>
              <Field label={label('modal.date')} htmlFor="tariffDate" required>
                <TextInput id="tariffDate" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
              </Field>
              <Field label={label('modal.subcontractor')} htmlFor="tariffSubcontractor" required>
                <SelectInput
                  id="tariffSubcontractor"
                  value={formData.isSubcontractor}
                  onChange={(e) => setFormData({ ...formData, isSubcontractor: e.target.value })}
                  required
                  placeholder={label('modal.choose')}
                  options={[{ value: 'Evet', label: label('yes') }, { value: 'Hayır', label: label('no') }]}
                />
              </Field>
            </div>

            <Field label={label('modal.info')} htmlFor="tariffInfo">
              <TextArea id="tariffInfo" rows={3} value={formData.info} onChange={(e) => setFormData({ ...formData, info: e.target.value })} />
            </Field>
          </div>

          {/* Sağ: FTE değerleri ya da saat ücreti */}
          <div className={PANEL}>
            <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
              {formData.type === 'Saat' ? <LuClock3 aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" /> : <LuBriefcaseBusiness aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />}
              {formData.type === 'Saat' ? label('modal.hourlyTitle') : label('modal.fteTitle')}
            </h3>

            {!formData.type && <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('modal.chooseTypeFirst')}</p>}

            {formData.type === 'Saat' && (
              <div className="tw-space-y-4">
                <Field label={label('modal.hourlyRate')} htmlFor="tariffHourlyRate" required>
                  <TextInput id="tariffHourlyRate" type="number" value={formData.hourlyRate} onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })} required className="tw-tabular-nums" />
                </Field>
                <Field label={label('modal.raise')} htmlFor="tariffHourlyRaise" hint={label('modal.raiseHintHourly')}>
                  <div className="tw-flex tw-gap-2">
                    <TextInput
                      id="tariffHourlyRaise"
                      type="number"
                      placeholder={label('modal.raisePlaceholder')}
                      className="tw-tabular-nums"
                      onChange={(e) => {
                        const percentage = parseFloat(e.target.value) || 0;
                        // Store the entered percentage for later use
                        setFormData((prev) => ({
                          ...prev,
                          percentageIncrease: percentage,
                        }));
                      }}
                    />
                    <Button onClick={applyHourlyPercentage} className="tw-shrink-0">
                      <LuPercent aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600 dark:tw-text-teal-300" />
                      {label('modal.apply')}
                    </Button>
                  </div>
                </Field>
              </div>
            )}

            {formData.type === 'FTE' && (
              <FteValuesEditor
                isEdit={isEdit}
                tariffValues={tariffValues}
                defaultPrice={defaultPrice}
                onDefaultPriceChange={handleDefaultPriceChange}
                onToggle={handleTariffToggle}
                onPriceChange={handleIndividualPriceChange}
                onApplyPercentage={applyFtePercentage}
                percentInputId="ftePercentageInput"
              />
            )}
          </div>
        </div>

        {formData.projects.length > 0 && (
          <ExistingTariffs
            projectIds={formData.projects}
            projects={projects}
            projectsWithTariffs={projectsWithTariffs}
            records={existingTariffRecords}
            loading={isLoadingExistingTariffs}
          />
        )}
      </form>
    </Modal>
  );
};

AddTariffsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedTariff: PropTypes.object,
  preselectedCustomer: PropTypes.string,
  preselectedProject: PropTypes.string,
};

export default AddTariffsModal;
